import {
  IRIS_NOTE_MAX,
  IRIS_TURN_MAX,
  IRIS_TURNS_MAX,
  irisOpening,
  parseIrisStep,
  type IrisCap,
  type IrisStep,
  type IrisStepAnswer,
  type IrisStepRequest,
  type IrisTurn,
  type IrisUsage,
} from '@engineer-os/engine';
import { hiddenFailures, objectiveState, verdict } from './grade';
import { newConversationId } from './mentor';
import type { GradeResult, PublicMission } from '../types';

/**
 * Iris in agent mode, as the tab holds her (docs/iris.md).
 *
 * One call to `POST /api/iris/step` is one model turn; the loop is the tab's.
 * The Worker is sent the whole conversation every time and keeps none of it,
 * so everything that makes this a conversation is here: the turns as the
 * model reads them, what the panel draws beside them, and the last numbers
 * the Worker reported. The user turns themselves are built by the engine's
 * helpers (`irisOpening`, `irisOutput`, …), which the Worker's measurement
 * script uses too, so a turn the tab sends is the turn that was priced.
 *
 * Modelled on `lib/mentor.ts`: the same credentials and cache rules, the same
 * client-minted conversation id, and the same rule that every failure is a
 * value. There is nothing underneath a refused step to catch a throw, and a
 * branch forgotten in a try/catch would be a panel that stopped moving.
 */

/** What the learner decided about a step, once they have. `pending` is a step still on the table. */
export type IrisDecision = 'pending' | 'ran' | 'rejected' | 'noted' | 'checked' | 'done';

/**
 * One thing on the panel, oldest first.
 *
 * Kept beside the turns rather than derived from them, because the two are
 * not the same text: a turn is what the model reads (`The learner says: …`),
 * an entry is what the learner reads (their own words, a command's output as
 * Iris was sent it). Drawing the turns would put the wire in front of the
 * learner; drawing only the entries would lose nothing the model needs.
 */
export type IrisEntry =
  | { kind: 'said'; text: string }
  | { kind: 'step'; step: IrisStep; decision: IrisDecision }
  /** Exactly the turn Iris is sent: output cut where hers is cut. */
  | { kind: 'output'; text: string }
  | { kind: 'check'; text: string };

/** Every answer that is not a step. */
export type IrisRefusal = Exclude<IrisStepAnswer['outcome'], 'step'>;

/**
 * A conversation, which belongs to one level and one workspace.
 *
 * State and not storage, for the mentor's reason (lib/mentor.ts): nothing
 * here is written anywhere, a reload starts a new one, and a reset or a level
 * change replaces it outright, id and all.
 */
export interface IrisConversation {
  id: string;
  turns: IrisTurn[];
  log: IrisEntry[];
  /** What the Worker last reported, or null before its first step. */
  usage: IrisUsage | null;
  /** What stopped the last request, or null. Cleared by the next step that arrives. */
  refusal: IrisRefusal | null;
  /**
   * Which cap a `capped` refusal hit, when the Worker said: the day's, which
   * returns tomorrow, or the conversation's, which a new conversation lifts.
   * Null for every other refusal, and for a `capped` that did not say.
   */
  cap: IrisCap | null;
}

export function newIrisConversation(refusal: IrisRefusal | null = null): IrisConversation {
  return { id: newConversationId(), turns: [], log: [], usage: null, refusal, cap: null };
}

/** The step waiting for the learner's decision, or null. Only ever the newest entry. */
export function pendingStep(c: IrisConversation): IrisStep | null {
  const last = c.log[c.log.length - 1];
  return last?.kind === 'step' && last.decision === 'pending' ? last.step : null;
}

/**
 * Whether there is a user turn the model has not answered.
 *
 * True after a refusal that left the learner's last turn unanswered, and that
 * is what makes "Try again" a resend of the same conversation rather than a
 * new one.
 */
export function awaitingIris(c: IrisConversation): boolean {
  return c.turns.length > 0 && c.turns[c.turns.length - 1]!.role === 'user';
}

/**
 * The refusals after which asking again, in this conversation, is pointless.
 *
 * `spent` is the account's cap for the mentor and Iris together, `budget` is
 * this attempt's tokens on a checkpoint, and `not-here` is a level this
 * account's Iris does not work on. None of them lifts by being asked again,
 * so the panel takes the composer away rather than inviting a press that will
 * be refused.
 */
export function irisEnded(c: IrisConversation): boolean {
  return c.refusal === 'spent' || c.refusal === 'budget' || c.refusal === 'not-here';
}

const clampTurn = (text: string) => text.slice(0, IRIS_TURN_MAX);

/**
 * Add a user turn, settling the step on the table if there is one.
 *
 * Two user turns in a row are merged into one. That happens when a request
 * was refused and the learner writes again rather than pressing Try again,
 * and the Worker checks roles: a conversation it would refuse for its shape
 * is a conversation that can never move again.
 */
export function withUser(
  c: IrisConversation,
  text: string,
  entries: IrisEntry[] = [],
  decision?: Exclude<IrisDecision, 'pending'>,
): IrisConversation {
  const log = c.log.map((e, i) =>
    decision && i === c.log.length - 1 && e.kind === 'step' && e.decision === 'pending' ? { ...e, decision } : e,
  );
  const last = c.turns[c.turns.length - 1];
  const turns = last?.role === 'user'
    ? [...c.turns.slice(0, -1), { role: 'user' as const, text: clampTurn(`${last.text}\n\n${text}`) }]
    : [...c.turns, { role: 'user' as const, text: clampTurn(text) }];
  return { ...c, turns, log: [...log, ...entries] };
}

/** The first turn: the brief, where the terminal starts, and the learner's request if they wrote one. */
export function openIris(c: IrisConversation, brief: string, cwd: string, request = ''): IrisConversation {
  const asked = request.trim().slice(0, IRIS_NOTE_MAX);
  return withUser({ ...c, refusal: null, cap: null }, irisOpening(brief, cwd, asked), asked ? [{ kind: 'said', text: asked }] : []);
}

/** Only a fixed crash classification leaves the terminal, never its contents. */
export function terminalRuntimeFailure(terminal: string | undefined): string | undefined {
  if (!terminal) return undefined;
  const commands = [...terminal.matchAll(/^[^\n]*\$[ \t]*node[ \t]+[^\n]*--test(?=[ \t\n]|$)[^\n]*\n/gm)];
  const latest = commands.at(-1);
  if (!latest || !/^[^\n]*Segmentation fault\b/i.test(terminal.slice(latest.index! + latest[0].length))) return undefined;
  return 'The learner\'s terminal shows that node --test ended with Segmentation fault. No normal test result was produced. Treat this as a runtime failure, separate from the level\'s failing assertions.';
}

/**
 * A step arrived. The model's turn is its JSON, which is what the Worker's
 * prompt asks for and what `parseIrisStep` reads back; `done` is settled on
 * arrival, because there is nothing to decide about it.
 */
export function withStep(c: IrisConversation, step: IrisStep, usage: IrisUsage): IrisConversation {
  return {
    ...c,
    turns: [...c.turns, { role: 'model', text: JSON.stringify({ say: step.say, action: step.action }) }],
    log: [...c.log, { kind: 'step', step, decision: step.action.kind === 'done' ? 'done' : 'pending' }],
    usage,
    refusal: null,
    cap: null,
  };
}

/**
 * A request that produced no step.
 *
 * `not-yours` is the one that changes the conversation rather than stopping
 * it: the Worker will not continue that id, so a fresh one is the answer, and
 * the log goes with it because the Worker has just said it is not this
 * conversation's. Everything else keeps the conversation as it was, so the
 * same turns can be sent again.
 */
export function withRefusal(c: IrisConversation, refusal: IrisRefusal, cap: IrisCap | null = null): IrisConversation {
  if (refusal === 'not-yours') return newIrisConversation('not-yours');
  return { ...c, refusal, cap: refusal === 'capped' ? cap : null };
}

/** The sentence a refusal is said with. `capped` has two, one for each cap. */
export type IrisRefusalSentence = IrisRefusal | 'capped-day';

/**
 * Which sentence the panel says for the conversation's refusal, or null.
 *
 * The day's cap is "come back tomorrow", because a new conversation would be
 * refused the same way; the conversation's, and a `capped` that did not say
 * which, is "start a new conversation", which is what lifts it.
 */
export function refusalSentence(c: IrisConversation): IrisRefusalSentence | null {
  if (!c.refusal) return null;
  return c.refusal === 'capped' && c.cap === 'day' ? 'capped-day' : c.refusal;
}

/* ── What Check my work said, as the learner saw it ─────────────────────── */

/**
 * The summary `irisCheck` wraps: the verdict line, every objective the rail
 * shows with its mark, the hidden checks that failed (the only moment the rail
 * names one), and whatever else the screen said under them.
 *
 * Built from what was on screen and nothing more. On a checkpoint Iris is
 * being assessed alongside the learner, and a summary that told her more than
 * the rail told them would make the tab the thing that caught her mistakes.
 * The words around the titles are English because they are said to the
 * model; the titles and messages are the ones on screen, in the learner's
 * language.
 */
export function irisCheckSummary(mission: PublicMission, grade: GradeResult | null, notes: readonly string[] = []): string {
  const lines: string[] = [];
  if (grade) {
    const said = verdict(mission, grade);
    lines.push(`${said.headline} ${said.detail}`.trim());
    const byId = new Map(grade.objectives.map((o) => [o.id, o]));
    for (const o of mission.objectives) {
      const r = byId.get(o.id);
      const state = objectiveState(r);
      const mark = state === 'is-pass' ? 'holds' : state === 'is-fail' ? 'fails' : state === 'is-skip' ? 'not checked yet' : 'not run';
      lines.push(`- [${mark}] ${o.title}${state === 'is-fail' && r?.message ? `: ${r.message}` : ''}`);
    }
    for (const r of hiddenFailures(grade)) {
      lines.push(`- [fails] Hidden check: ${r.title}${r.message ? `: ${r.message}` : ''}`);
    }
  }
  for (const note of notes) if (note.trim()) lines.push(note.trim());
  return lines.length > 0 ? lines.join('\n') : 'Check my work gave no result.';
}

/* ── The wire ───────────────────────────────────────────────────────────── */

const REFUSALS: readonly IrisRefusal[] = [
  'sign-in', 'not-here', 'not-yours', 'spent', 'budget', 'capped', 'busy', 'unavailable', 'filtered', 'refused',
];

const isRefusal = (value: unknown): value is IrisRefusal =>
  typeof value === 'string' && (REFUSALS as readonly string[]).includes(value);

/**
 * A refusal as the answer carries it. Only `capped` carries more than its
 * word: the Worker's `cap` and `resetAt`, when the body has them and they are
 * ones this tab knows.
 */
function refusalAnswer(outcome: IrisRefusal, payload: Record<string, unknown>): IrisStepAnswer {
  if (outcome !== 'capped') return { outcome };
  const cap = payload.cap === 'day' || payload.cap === 'conversation' ? payload.cap : undefined;
  const resetAt = typeof payload.resetAt === 'number' && Number.isFinite(payload.resetAt) ? payload.resetAt : undefined;
  return { outcome, ...(cap ? { cap } : {}), ...(resetAt !== undefined ? { resetAt } : {}) };
}

function readUsage(value: unknown): IrisUsage | null {
  if (!value || typeof value !== 'object') return null;
  const u = value as Record<string, unknown>;
  const n = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? Math.max(0, v) : 0);
  return {
    tokens: n(u.tokens),
    conversationTokens: n(u.conversationTokens),
    budget: typeof u.budget === 'number' && Number.isFinite(u.budget) && u.budget > 0 ? u.budget : null,
  };
}

/**
 * The refusal a status implies when the body did not name one.
 *
 * The word in the body wins wherever there is one, because four refusals
 * share 429 and two share 403; this table is only for a body that did not
 * say, and an unknown one is `unavailable` — "Iris could not be reached" is
 * the one sentence true of every unexplained failure.
 */
function refusalFor(status: number, word: unknown): IrisRefusal {
  if (isRefusal(word)) return word;
  if (status === 401) return 'sign-in';
  if (status === 403) return 'not-here';
  if (status === 409) return 'busy';
  if (status === 429) return 'capped';
  return 'unavailable';
}

/**
 * One step.
 *
 * Never rejects, including on abort. A body carrying `outcome` is read as the
 * contract's `IrisStepAnswer`; one carrying only `error` or `reason`, as the
 * mentor's routes answer, is read by that word. A 200 whose step does not
 * parse is `unavailable` rather than a step drawn from half a shape: a
 * command the learner might approve has to be exactly the command Iris sent.
 */
export async function askIris(request: IrisStepRequest, signal?: AbortSignal): Promise<IrisStepAnswer> {
  if (request.turns.length > IRIS_TURNS_MAX) return { outcome: 'capped', cap: 'conversation' };
  let response: Response;
  let value: unknown;
  try {
    response = await fetch('/api/iris/step', {
      method: 'POST',
      credentials: 'same-origin',
      cache: 'no-store',
      signal,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });
    value = await response.json().catch(() => null);
  } catch {
    return { outcome: 'unavailable' };
  }
  const payload = (value && typeof value === 'object' ? value : {}) as Record<string, unknown>;
  if (response.ok && (payload.outcome === 'step' || (payload.outcome === undefined && payload.step))) {
    const step = parseIrisStep(JSON.stringify(payload.step ?? null));
    const usage = readUsage(payload.usage);
    return step && usage ? { outcome: 'step', step, usage } : { outcome: 'unavailable' };
  }
  if (isRefusal(payload.outcome)) return refusalAnswer(payload.outcome, payload);
  if (response.ok) return refusalAnswer(isRefusal(payload.reason) ? payload.reason : 'unavailable', payload);
  return refusalAnswer(refusalFor(response.status, payload.error ?? payload.reason), payload);
}
